import React, { useCallback, useState, useEffect } from "react";
import { useDropzone } from "react-dropzone";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import {
  collection,
  addDoc,
  query,
  where,
  getDocs,
  Timestamp,
  doc,
  updateDoc,
} from "firebase/firestore";
import { storage, db } from "../firebase";
import axios from "axios";
import { track } from "@vercel/analytics";
import "./ResumeUpload.css";

// Add constants for rate limiting and file size
const MAX_FILE_SIZE = 2.5 * 1024 * 1024; // 2.5MB max file size
const MAX_UPLOADS_PER_HOUR = 2; // Maximum uploads per hour
const RATE_LIMIT_HOURS = 1; // Time window for rate limiting in hours
const RATE_CHECK_INTERVAL = 30000; // Check rate limit every 30 seconds

// Function to get API URL based on environment
const getApiUrl = () => {
  if (process.env.NODE_ENV === "production") {
    const host = window.location.host.replace(/^@/, "");
    return `https://${host}`;
  }
  return process.env.REACT_APP_API_URL || "http://localhost:5002/api";
};

// Simple logging function
const safeLog = (message, error) => {
  if (!error) {
    console.log(message);
    return;
  }
  console.error(message, {
    name: error.name,
    message: error.message?.replace(/https?:\/\/[^\s]+/g, "[URL_REDACTED]"),
    status: error.response?.status,
  });
};

// Function to get user-friendly error messages
const getSafeErrorMessage = (error) => {
  if (!error) return "An unknown error occurred";

  if (error.response) {
    switch (error.response.status) {
      case 400:
        return "Invalid request. Please check your file and try again.";
      case 401:
      case 403:
        return "You don't have permission to perform this action.";
      case 404:
        return "The requested resource was not found.";
      case 413:
        return "Your resume file is too large. Please upload a smaller file.";
      case 429:
        return "You've reached the maximum number of resume analyses allowed. Please try again later.";
      case 500:
      case 502:
      case 503:
      case 504:
        return "Server error. Our team has been notified and is working on it.";
      default:
        return `Server error (${error.response.status}). Please try again later.`;
    }
  } else if (error.request) {
    return "No response from server. Please check your connection and try again.";
  } else if (error.message?.includes("timeout")) {
    return "Request timed out. Please try again later.";
  } else if (error.message?.includes("Network Error")) {
    return "Network error. Please check your connection and try again.";
  }

  return "An error occurred while processing your request.";
};

const ResumeUpload = ({ setReviewData, setIsLoading }) => {
  const [file, setFile] = useState(null);
  const [error, setError] = useState("");
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [uploadCount, setUploadCount] = useState(0);
  const [isRateLimited, setIsRateLimited] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState(0);
  const [previousAnalyses, setPreviousAnalyses] = useState([]);
  const [showPrevious, setShowPrevious] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Simple function to check if the error message indicates rate limiting
  const isRateLimitError = () => {
    return error && error.toLowerCase().includes("rate limit exceeded");
  };

  // Check rate limit and fetch previous analyses on component mount
  useEffect(() => {
    checkRateLimit();
    fetchPreviousAnalyses();

    // Set up regular interval to check rate limit
    const intervalId = setInterval(() => {
      checkRateLimit();
    }, RATE_CHECK_INTERVAL);

    // Clean up interval on component unmount
    return () => clearInterval(intervalId);
  }, []);

  // Function to check rate limit status
  const checkRateLimit = async () => {
    try {
      const apiBaseUrl = getApiUrl();
      const response = await axios({
        method: "head",
        url: `${apiBaseUrl}/api/analyze-resume`,
        timeout: 5000,
        withCredentials: true,
      });

      if (response.headers) {
        const rateLimit = parseInt(
          response.headers["x-ratelimit-limit"] || MAX_UPLOADS_PER_HOUR
        );
        const rateRemaining = parseInt(
          response.headers["x-ratelimit-remaining"] || MAX_UPLOADS_PER_HOUR
        );

        setUploadCount(Math.max(0, rateLimit - rateRemaining));

        if (rateRemaining <= 0) {
          const retryAfter = response.headers["retry-after"]
            ? Math.ceil(parseInt(response.headers["retry-after"]) / 60)
            : 60;

          setIsRateLimited(true);
          setTimeRemaining(retryAfter);
          setError(
            `Rate limit exceeded. Please try again in ${retryAfter} minutes.`
          );

          // Clear file if rate limited
          if (file) setFile(null);

          return false;
        } else {
          setIsRateLimited(false);
          return true;
        }
      }
      return true;
    } catch (error) {
      safeLog("Error checking rate limit", error);
      return true; // Being lenient on errors
    }
  };

  // Function to fetch previous analyses
  const fetchPreviousAnalyses = async () => {
    try {
      const userId = sessionStorage.getItem("userId");
      if (!userId) return;

      const analysesRef = collection(db, "resumes");
      const q = query(
        analysesRef,
        where("userId", "==", userId),
        where("status", "==", "completed")
      );

      const querySnapshot = await getDocs(q);
      const analyses = [];

      querySnapshot.forEach((doc) => {
        const data = doc.data();
        analyses.push({
          id: doc.id,
          fileName: data.fileName,
          uploadedAt: data.uploadedAt.toDate(),
          roast: data.roast,
          feedback: data.feedback,
        });
      });

      analyses.sort((a, b) => b.uploadedAt - a.uploadedAt);
      setPreviousAnalyses(analyses);
    } catch (error) {
      safeLog("Error fetching previous analyses", error);
    }
  };

  // Function to load a previous analysis
  const loadPreviousAnalysis = (analysis) => {
    setReviewData({
      roast: analysis.roast,
      feedback: analysis.feedback,
      isDefaultResponse: false,
    });
    setShowPrevious(false);
  };

  // Handle file drop
  const onDrop = useCallback(
    (acceptedFiles) => {
      // Don't process files if rate limited or if we have a rate limit error
      if (isRateLimited || isRateLimitError()) {
        setError(`Maximum of 2 submissions per hour. Please try again later.`);
        return;
      }

      const selectedFile = acceptedFiles[0];
      if (!selectedFile) {
        setError("No file selected");
        setFile(null);
        return;
      }

      // Check if file is a PDF
      const fileExtension = selectedFile.name.split(".").pop().toLowerCase();
      const isPDF =
        selectedFile.type === "application/pdf" ||
        selectedFile.type === "application/x-pdf" ||
        selectedFile.type === "application/acrobat" ||
        selectedFile.type === "application/vnd.pdf" ||
        fileExtension === "pdf";

      if (!isPDF) {
        setError("Please upload a PDF file only");
        setFile(null);
        return;
      }

      // Check file size
      if (selectedFile.size > MAX_FILE_SIZE) {
        setError(
          `File size exceeds maximum limit of ${
            MAX_FILE_SIZE / 1024 / 1024
          }MB. Your file is ${(selectedFile.size / 1024 / 1024).toFixed(2)}MB.`
        );
        setFile(null);
        return;
      }

      // Don't set the file if we have a rate limit error
      if (!isRateLimitError()) {
        setFile(selectedFile);
        setError("");
      }
    },
    [isRateLimited, timeRemaining, error]
  );

  // Configure dropzone
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      "application/pdf": [".pdf"],
      "application/x-pdf": [".pdf"],
      "application/acrobat": [".pdf"],
      "application/vnd.pdf": [".pdf"],
    },
    maxFiles: 1,
    disabled: isRateLimited || isRateLimitError(),
    noClick: isRateLimited || isRateLimitError(),
    noKeyboard: isRateLimited || isRateLimitError(),
  });

  // Handle form submission
  const handleSubmit = async () => {
    // Don't proceed if rate limited or if we have a rate limit error
    if (isRateLimited || isRateLimitError()) {
      setError(`Maximum of 2 submissions per hour. Please try again later.`);
      return;
    }

    // Clear previous errors if not rate limited
    if (!isRateLimitError()) {
      setError("");
    }

    // Basic validation
    if (!file) {
      setError("Please upload a resume first");
      return;
    }

    if (!termsAccepted) {
      setError("Please accept the terms and conditions to continue");
      return;
    }

    // Prevent double submission
    if (isSubmitting) {
      return;
    }

    setIsSubmitting(true);
    setIsLoading(true);

    try {
      // Do a fresh rate limit check before proceeding
      const notRateLimited = await checkRateLimit();

      if (!notRateLimited || isRateLimited || isRateLimitError()) {
        setError(`Maximum of 2 submissions per hour. Please try again later.`);
        setIsLoading(false);
        setIsSubmitting(false);
        return;
      }

      // Track upload attempt
      track("resume_upload_started", {
        fileSize: Math.round(file.size / 1024),
        fileType: file.type,
      });

      // Get or create user ID
      let userId = sessionStorage.getItem("userId");
      if (!userId) {
        userId = `user_${Date.now()}_${Math.random()
          .toString(36)
          .substring(2, 15)}`;
        sessionStorage.setItem("userId", userId);
      }

      // Upload to Firebase Storage
      const storageRef = ref(storage, `resumes/${file.name}-${Date.now()}`);
      const uploadResult = await uploadBytes(storageRef, file);
      const downloadUrl = await getDownloadURL(uploadResult.ref);

      // Create Firestore document
      const docRef = await addDoc(collection(db, "resumes"), {
        fileName: file.name,
        fileSize: file.size,
        uploadedAt: Timestamp.now(),
        userId: userId,
        status: "processing",
      });

      safeLog("Resume uploaded successfully");

      // Call API to analyze resume
      const apiBaseUrl = getApiUrl();
      const apiEndpoint = `${apiBaseUrl}/api/analyze-resume`;

      const apiResponse = await axios.post(
        apiEndpoint,
        { url: downloadUrl },
        {
          timeout: 60000,
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
          withCredentials: true,
        }
      );

      // Update rate limit info from response headers
      if (apiResponse.headers && apiResponse.headers["x-ratelimit-remaining"]) {
        const rateRemaining = parseInt(
          apiResponse.headers["x-ratelimit-remaining"]
        );
        const rateLimit = parseInt(
          apiResponse.headers["x-ratelimit-limit"] || MAX_UPLOADS_PER_HOUR
        );
        setUploadCount(Math.max(0, rateLimit - rateRemaining));
      }

      // Track successful API response
      track("resume_analysis_success");

      // Update Firestore document
      await updateDoc(doc(db, "resumes", docRef.id), {
        status: "completed",
        analyzedAt: Timestamp.now(),
        roast: apiResponse.data.roast,
        feedback: apiResponse.data.feedback,
      });

      // Set review data
      setReviewData(apiResponse.data);
    } catch (error) {
      safeLog("Error in submit process", error);

      // Check for rate limit in error response
      if (error.response && error.response.status === 429) {
        setIsRateLimited(true);
        if (error.response.headers && error.response.headers["retry-after"]) {
          const retryAfter = Math.ceil(
            parseInt(error.response.headers["retry-after"]) / 60
          );
          setTimeRemaining(retryAfter);
        } else {
          setTimeRemaining(60); // Default to 60 minutes
        }
        setError(`Maximum of 2 submissions per hour. Please try again later.`);
      } else {
        // Only set non-rate-limit errors if we don't already have a rate limit error
        if (!isRateLimitError()) {
          setError(getSafeErrorMessage(error));
        }
      }

      track("resume_upload_error", { errorType: error.code || "unknown" });
    } finally {
      await checkRateLimit(); // Refresh rate limit info
      setIsLoading(false);
      setIsSubmitting(false);
    }
  };

  const openTermsAndConditions = (e) => {
    e.preventDefault();
    window.open(
      process.env.REACT_APP_TERMS_URL ||
        "https://drive.google.com/file/d/1_QH2lfPSs-Hsnxlo86tflGoYgEa0tDkf/view?usp=sharing",
      "_blank"
    );
  };

  // Determine if we should disable UI elements based on rate limit error
  const isDisabled = isRateLimited || isRateLimitError();

  // Function to render the dropzone content
  const renderDropzoneContent = () => {
    if (file) {
      return (
        <div className="file-info">
          <div className="file-icon">📄✨</div>
          <div className="file-name">{file.name}</div>
          <div className="file-size">
            {(file.size / 1024 / 1024).toFixed(2)} MB
          </div>
          {file.size > MAX_FILE_SIZE * 0.8 && (
            <div className="file-size-warning">
              Bestie, this file is kinda big! Try to keep it under 2.5MB.
            </div>
          )}
        </div>
      );
    }

    return (
      <>
        <div className="upload-icon">📂</div>
        <p>Drop your resume here or click to select</p>
        <p className="file-hint">
          <span className="vibe-check"></span> Supported format: PDF (Max 2.5MB)
        </p>
      </>
    );
  };

  // Inline styles for new elements
  const funHeaderStyle = {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    marginBottom: "1rem",
    position: "relative",
  };

  const funGifContainerStyle = {
    width: "100%",
    maxWidth: "200px",
    margin: "0.5rem auto",
    position: "relative",
    overflow: "hidden",
    borderRadius: "10px",
    boxShadow: "0 4px 8px rgba(0,0,0,0.1)",
  };

  const funGifStyle = {
    width: "100%",
    height: "auto",
    display: "block",
    transform: "scale(1.05)",
    transition: "transform 0.3s ease",
  };

  const genZQuoteStyle = {
    background:
      "linear-gradient(135deg, rgba(255,107,107,0.1), rgba(20,83,116,0.1))",
    padding: "12px 15px",
    borderRadius: "10px",
    marginBottom: "1.5rem",
    textAlign: "center",
    fontStyle: "italic",
    position: "relative",
  };

  const quoteTextStyle = {
    fontSize: "1.1rem",
    color: "var(--primary-color)",
    fontWeight: 500,
    margin: 0,
  };

  return (
    <div className="resume-upload-container">
      <div style={funHeaderStyle}>
        <h2>Let's Roast Your Resume</h2>
      </div>

      <p className="upload-description">
        Upload your resume and we'll give you the{" "}
        <span className="highlight-text">brutal truth</span> about it.
        <br />
        No sugar coating, just straight facts.{" "}
        <span className="emoji-text">💯</span>
      </p>

      <div style={genZQuoteStyle}>
        <p className="genZQuote">
          "Your resume is about to get more roasted than your Starbucks ☕"
        </p>
      </div>

      {isRateLimited && (
        <div className="rate-limit-warning">
          <p>
            <strong>Hold up! Too many roasts! 🔥</strong>
          </p>
          <p>
            You've hit the limit of {MAX_UPLOADS_PER_HOUR} resume roasts per
            hour.
          </p>
          <p>
            Try again in {timeRemaining} minutes or choose a previous roast
            below.
          </p>
          <p className="rate-limit-tip">
            Pro tip: Quality over quantity, bestie! Take time to fix your resume
            before trying again.
          </p>
        </div>
      )}

      {!isRateLimited && uploadCount > 0 && (
        <div className="rate-limit-info">
          <p>
            You've used {uploadCount} of {MAX_UPLOADS_PER_HOUR} resume roasts
            this hour. Choose wisely!
          </p>
        </div>
      )}

      {previousAnalyses.length > 0 && (
        <div className="previous-analyses">
          <button
            className="toggle-previous"
            onClick={() => setShowPrevious(!showPrevious)}
          >
            {showPrevious
              ? "Hide Previous Roasts 🙈"
              : "Show Previous Roasts 👀"}
          </button>

          {showPrevious && (
            <div className="analyses-list">
              {previousAnalyses.map((analysis) => (
                <div key={analysis.id} className="analysis-item">
                  <div className="analysis-info">
                    <div className="analysis-filename">{analysis.fileName}</div>
                    <div className="analysis-date">
                      {new Date(analysis.uploadedAt).toLocaleDateString()} at{" "}
                      {new Date(analysis.uploadedAt).toLocaleTimeString()}
                    </div>
                  </div>
                  <button
                    className="load-analysis-btn"
                    onClick={() => loadPreviousAnalysis(analysis)}
                    disabled={isRateLimited}
                  >
                    View This Roast 🔥
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <div
        {...getRootProps({
          className: `dropzone ${isDragActive ? "active" : ""} ${
            file ? "has-file" : ""
          } ${isRateLimited || isSubmitting ? "disabled" : ""}`,
        })}
      >
        <input {...getInputProps()} />
        {renderDropzoneContent()}
      </div>

      <div className="terms-container">
        <label className="terms-label">
          <input
            type="checkbox"
            className="terms-checkbox"
            checked={termsAccepted}
            onChange={(e) => setTermsAccepted(e.target.checked)}
            disabled={isRateLimited || isSubmitting}
          />
          <span>
            I agree to the{" "}
            <a href="#" onClick={openTermsAndConditions}>
              Terms & Conditions
            </a>{" "}
          </span>
        </label>
      </div>

      {error && <div className="error-message">{error}</div>}

      <button
        className="submit-button"
        onClick={handleSubmit}
        disabled={
          !file ||
          !termsAccepted ||
          isRateLimited ||
          isSubmitting ||
          isRateLimitError()
        }
      >
        {isSubmitting ? "Roasting..." : "Roast My Resume 🔥"}
      </button>

      <div className="downtime-notice">
        <p>
          <strong>Recent Downtime:</strong> 22 May 11:21:52 am
        </p>
        <p>
          We apologize for any inconvenience caused due to high traffic. The
          issue has been fixed.
        </p>
      </div>

      <p className="privacy-note">
        Your resume will be analyzed by AI and stored securely.
        <br />
        <span className="slang">Your data is not worthy of being gatekept</span>
      </p>
    </div>
  );
};

export default ResumeUpload;
